/**
 * BW20-H — banner on /log/meals/:date describing the recovery-state-driven
 * meal plan. State / Meaning / Next-move template, never prescriptive.
 *
 * Color is keyed off the recovery state: light green tint for Green,
 * amber tint for Amber, brick tint for Red. The pointer link (if present)
 * routes to /health/training for the deferred-session flow.
 */

import { Link } from "react-router-dom";
import type { DailyMealPlan } from "@/lib/meal-recommendations";

const TONE: Record<DailyMealPlan["recoveryState"], { bg: string; border: string }> = {
  green: { bg: "rgba(74, 156, 82, 0.06)", border: "rgba(74, 156, 82, 0.35)" },
  amber: { bg: "rgba(217, 154, 58, 0.08)", border: "rgba(217, 154, 58, 0.45)" },
  red: { bg: "rgba(176, 74, 48, 0.07)", border: "rgba(176, 74, 48, 0.45)" },
};

export function RecoveryMealBanner({ plan }: { plan: DailyMealPlan }) {
  const tone = TONE[plan.recoveryState];
  return (
    <section
      aria-label="Recovery-driven meal plan"
      style={{
        padding: "var(--space-3)",
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: "var(--radius-2)",
        fontSize: "var(--type-body-s)",
        lineHeight: 1.55,
        color: "var(--fg-2)",
      }}
    >
      <p style={{ margin: 0 }}>
        <strong style={{ color: "var(--fg-1)" }}>State.</strong> {plan.banner.state}
        <br />
        <strong style={{ color: "var(--fg-1)" }}>Meaning.</strong> {plan.banner.meaning}
        <br />
        <strong style={{ color: "var(--fg-1)" }}>Next move.</strong>{" "}
        {plan.banner.nextMove}{" "}
        {plan.banner.pointer && (
          <Link
            to={plan.banner.pointer.href}
            style={{
              color: "var(--fg-1)",
              textDecoration: "underline",
            }}
          >
            {plan.banner.pointer.label}
          </Link>
        )}
      </p>
    </section>
  );
}
